import { useCallback, useEffect, useRef } from 'react';

const PAGE_CONTENT_ID = 'scrollable-page-area';
const WALL_THICKNESS = 200;

const randomBetween = (min, max) => min + (Math.random() * (max - min));

const useGravityCollapse = (containerRef) => {
  const triggeredRef = useRef(false);
  const cleanupRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, []);

  const trigger = useCallback(async () => {
    if (triggeredRef.current || !containerRef.current) {
      return;
    }
    triggeredRef.current = true;

    const matterModule = await import('matter-js');
    const Matter = matterModule.default || matterModule;

    if (!mountedRef.current || !containerRef.current) {
      return;
    }

    const pageEl = document.getElementById(PAGE_CONTENT_ID) || containerRef.current;
    const pageRect = pageEl.getBoundingClientRect();
    const targets = Array.from(containerRef.current.querySelectorAll('[data-gravity]'));

    const engine = Matter.Engine.create();
    engine.enableSleeping = true;
    const { world } = engine;

    const floor = Matter.Bodies.rectangle(
      pageRect.left + (pageRect.width / 2),
      pageRect.bottom + (WALL_THICKNESS / 2),
      pageRect.width + (WALL_THICKNESS * 2),
      WALL_THICKNESS,
      { isStatic: true },
    );
    const wallHeight = pageRect.height + 800;
    const wallCenterY = pageRect.top + (pageRect.height / 2) - 200;
    const leftWall = Matter.Bodies.rectangle(
      pageRect.left - (WALL_THICKNESS / 2),
      wallCenterY,
      WALL_THICKNESS,
      wallHeight,
      { isStatic: true },
    );
    const rightWall = Matter.Bodies.rectangle(
      pageRect.right + (WALL_THICKNESS / 2),
      wallCenterY,
      WALL_THICKNESS,
      wallHeight,
      { isStatic: true },
    );

    const pairs = targets.reduce((accumulator, el) => {
      const rect = el.getBoundingClientRect();
      if (rect.width < 2 || rect.height < 2) {
        return accumulator;
      }

      const originX = rect.left + (rect.width / 2);
      const originY = rect.top + (rect.height / 2);
      const maxCenterY = pageRect.bottom - (rect.height / 2) - 4;
      const centerY = Math.min(originY, maxCenterY);
      const chamferRadius = Math.min(8, (rect.width / 2) - 1, (rect.height / 2) - 1);

      const body = Matter.Bodies.rectangle(originX, centerY, rect.width, rect.height, {
        restitution: 0.25,
        friction: 0.5,
        frictionAir: 0.01,
        chamfer: { radius: Math.max(0, chamferRadius) },
      });

      Matter.Body.setAngle(body, randomBetween(-0.8, 0.8));
      Matter.Body.setVelocity(body, {
        x: randomBetween(-4, 4),
        y: randomBetween(-6, -1),
      });
      Matter.Body.setAngularVelocity(body, randomBetween(-0.2, 0.2));

      const node = el;
      node.style.willChange = 'transform';
      node.style.position = 'relative';
      node.style.zIndex = '1';
      node.style.transformOrigin = 'center center';

      accumulator.push({
        el,
        body,
        originX,
        originY,
      });
      return accumulator;
    }, []);

    Matter.Composite.add(world, [
      floor,
      leftWall,
      rightWall,
      ...pairs.map(({ body }) => body),
    ]);

    let frameId = 0;
    let stopped = false;

    const step = () => {
      if (stopped) {
        return;
      }
      Matter.Engine.update(engine, 1000 / 60);
      pairs.forEach((pair) => {
        const {
          body, originX, originY,
        } = pair;
        const node = pair.el;
        const dx = body.position.x - originX;
        const dy = body.position.y - originY;
        node.style.transform = `translate(${dx}px, ${dy}px) rotate(${body.angle}rad)`;
      });
      if (pairs.every(({ body }) => body.isSleeping)) {
        stopped = true;
        return;
      }
      frameId = requestAnimationFrame(step);
    };

    frameId = requestAnimationFrame(step);

    cleanupRef.current = () => {
      stopped = true;
      cancelAnimationFrame(frameId);
      Matter.Composite.clear(world, false);
      Matter.Engine.clear(engine);
    };
  }, [containerRef]);

  return trigger;
};

export default useGravityCollapse;
